import React from "react";
import { connect } from "react-redux";
import { Redirect, Route, Switch, withRouter } from "react-router-dom";
import asyncComponent from "util/asyncComponent";
import ChatNew from "../components/chat/ChatNew/ChatNew";
import { BULK_MESSAGING } from "../util/permissions";
import { AccountStatus } from "../util/account";
import { isHiddenInShoper, isShoperLimited } from "../util/shoperLimits";

// Created once, at module level. An asyncComponent built inside render is a new component class on every
// router render, so any change of the URL (even just the query string) would unmount and remount the page.
const LazyPlansPlanlist = asyncComponent(() => import('./plans/planList'));
const LazyPayments = asyncComponent(() => import('./payments'));
const LazyBilling = asyncComponent(() => import('./billing'));
const LazyPlansNewplan = asyncComponent(() => import('./plans/newPlan'));
const LazyPlansSmsplan = asyncComponent(() => import('./plans/smsPlan'));
const LazyPlansWhatsappplan = asyncComponent(() => import('./plans/whatsAppPlan'));
const LazyShoperLogin = asyncComponent(() => import('./shoper/login'));
const LazyShoperMobilelogin = asyncComponent(() => import('./shoper/mobileLogin'));
const LazyChannelsMobilelogin = asyncComponent(() => import('./channels/mobileLogin'));
const LazyChannelsChooseaccount = asyncComponent(() => import('./channels/chooseAccount'));
const LazyChannelsLogin = asyncComponent(() => import('./channels/login'));
const LazyLivechatLogin = asyncComponent(() => import('./livechat/login'));
const LazyAwsmarketplaceLogin = asyncComponent(() => import('./awsMarketplace/login'));
const LazyVideosVideoplayer = asyncComponent(() => import('./videos/videoPlayer'));
const LazyVideosVideomobileuploader = asyncComponent(() => import('./videos/videoMobileUploader'));
const LazyPrinttagsArtworkMobileUploader = asyncComponent(() => import('./printTags/ArtworkMobileUploader'));
const LazyQrandnfc = asyncComponent(() => import('./qrAndNfc'));
const LazyQrcode = asyncComponent(() => import('./qrCode'));
const LazyNewsletter = asyncComponent(() => import('./newsletter'));
const LazyNewsletterunsubscribingform = asyncComponent(() => import('./newsletterUnsubscribingForm'));
const LazyNewsletterunsubscribing = asyncComponent(() => import('./newsletterUnsubscribing'));
const LazyAwsmarketplaceSetupform = asyncComponent(() => import('./awsMarketplace/setupForm'));
const LazyNophonenumber = asyncComponent(() => import('./NoPhoneNumber'));
const LazyWidgetNewsletterdashboard = asyncComponent(() => import('./widget/newsletterDashboard'));
const LazySettingsLivechat = asyncComponent(() => import('./settings/livechat'));
const LazySettingsIntercom = asyncComponent(() => import('./settings/intercom'));
const LazySettingsSlack = asyncComponent(() => import('./settings/slack'));
const LazyBillingdata = asyncComponent(() => import('./billingData'));
const LazyPhonenumbers = asyncComponent(() => import('./phoneNumbers'));
const LazyFormtextsFormtextedit = asyncComponent(() => import('./formTexts/FormTextEdit'));
const LazyFormtexts = asyncComponent(() => import('./formTexts'));
const LazyMessagetemplatesMessagetemplateadd = asyncComponent(() => import('./messageTemplates/messageTemplateAdd'));
const LazyMessagetemplatesMessagetemplateedit = asyncComponent(() => import('./messageTemplates/messageTemplateEdit'));
const LazyMessagetemplates = asyncComponent(() => import('./messageTemplates'));
const LazySavedReplies = asyncComponent(() => import('./savedReplies'));
const LazyBulkMessage = asyncComponent(() => import('./bulk/message'));
const LazyContactsContactsfileimport = asyncComponent(() => import('./contacts/contactsFileImport'));
const LazyContactsContactdetails = asyncComponent(() => import('./contacts/contactDetails'));
const LazyContactsHub = asyncComponent(() => import('./contacts/hub'));
const LazySettings = asyncComponent(() => import('./settings'));
const LazySettingsUnsubscribingpagesettings = asyncComponent(() => import('./settings/unsubscribingPageSettings'));
const LazyUrlsUrladd = asyncComponent(() => import('./urls/urlAdd'));
const LazyUrlsUrledit = asyncComponent(() => import('./urls/urlEdit'));
const LazyUrls = asyncComponent(() => import('./urls'));
const LazyVideosVideoedit = asyncComponent(() => import('./videos/videoEdit'));
const LazyMedia = asyncComponent(() => import('./media'));
const LazyBulkDetails = asyncComponent(() => import('./bulk/details'));
const LazyBulkReports = asyncComponent(() => import('./bulk/reports'));
const LazyReports = asyncComponent(() => import('./reports'));
const LazyAnalytics = asyncComponent(() => import('./analytics'));
const LazyCampaignkeywordsCampaignkeywordedit = asyncComponent(() => import('./campaignKeywords/campaignKeywordEdit'));
const LazyCampaignkeywordsCampaignkeywordadd = asyncComponent(() => import('./campaignKeywords/campaignKeywordAdd'));
const LazyAutomations = asyncComponent(() => import('./automations'));
const LazyCampaignkeywords = asyncComponent(() => import('./campaignKeywords'));
const LazyWebhooksWebhookdetails = asyncComponent(() => import('./webhooks/webhookDetails'));
const LazyWebhooks = asyncComponent(() => import('./webhooks'));
const LazyNfctaggroupsNfctaggroupadd = asyncComponent(() => import('./nfcTagGroups/nfcTagGroupAdd'));
const LazyNfctaggroupsNfctaggroupedit = asyncComponent(() => import('./nfcTagGroups/nfcTagGroupEdit'));
const LazyWidgetPopupsPopupedit = asyncComponent(() => import('./widget/popups/popupEdit'));
const LazyWidgetPopupsPopupadd = asyncComponent(() => import('./widget/popups/popupAdd'));
const LazyWidgetPopupsPopupinstallation = asyncComponent(() => import('./widget/popups/popupInstallation'));
const LazyWidgetForms = asyncComponent(() => import('./widget/forms'));
const LazyCouponsCouponimportslist = asyncComponent(() => import('./coupons/couponImportsList'));
const LazyCouponsCouponsimport = asyncComponent(() => import('./coupons/couponsImport'));
const LazyCouponsCouponslist = asyncComponent(() => import('./coupons/couponsList'));
const LazyWidgetLandingpagesLandingpageedit = asyncComponent(() => import('./widget/landingPages/landingPageEdit'));
const LazyWidgetLandingpagesLandingpageadd = asyncComponent(() => import('./widget/landingPages/landingPageAdd'));
const LazyWidgetUnsubscribingpagesUnsubscribingpageedit = asyncComponent(() => import('./widget/unsubscribingPages/unsubscribingPageEdit'));
const LazyWidgetUnsubscribingpagesUnsubscribingpageadd = asyncComponent(() => import('./widget/unsubscribingPages/unsubscribingPageAdd'));
const LazyWidgetEmbeddednewslettersEmbeddednewsletteredit = asyncComponent(() => import('./widget/embeddedNewsletters/embeddedNewsletterEdit'));
const LazyWidgetEmbeddednewslettersEmbeddednewsletteradd = asyncComponent(() => import('./widget/embeddedNewsletters/embeddedNewsletterAdd'));
const LazyWidgetEmbeddednewslettersEmbeddednewsletterinstallation = asyncComponent(() => import('./widget/embeddedNewsletters/embeddedNewsletterInstallation'));
const LazyWidgetIntegrations = asyncComponent(() => import('./widget/integrations'));
const LazyQrcodesQrcodeadd = asyncComponent(() => import('./qrCodes/qrCodeAdd'));
const LazyQrcodesQrcodeedit = asyncComponent(() => import('./qrCodes/qrCodeEdit'));
const LazyNfcsignsNfcsignedit = asyncComponent(() => import('./nfcSigns/nfcSignEdit'));
const PRINT_TAG_ORDER_ID = '[0-9a-fA-F]{24}';
const LazyPrinttags = asyncComponent(() => import('./printTags'));
const LazyPrinttagsDesigner = asyncComponent(() => import('./printTags/designer'));

// Pages were remounted on every router render before the route components were hoisted. Keeping that for
// path and query changes (many pages read ?tab=, ?new=, ?copyId=, ?id= only when they mount) and dropping it
// where a page owns its query string: the report viewer keeps its filters in the URL, so /reports must
// survive query edits (opening another report is a plain list -> viewer change inside the same page).
const remountKey = ({pathname, search, state}) => {
    // The router matches case-insensitively and ignores a trailing slash: /Reports/ is the reports page too.
    const normalised = pathname.toLowerCase().replace(/\/$/, '');
    // The first "Save draft" of a new tag replaces /print-tags/new with /print-tags/<id>/edit: the designer
    // stays mounted with what is on screen (it passes the key it had).
    const kept = typeof state?.printTagsKeepDesigner === 'string' ? state.printTagsKeepDesigner : null;
    if (kept && kept.split('?')[0].toLowerCase().replace(/\/$/, '') === '/print-tags/new'
        && /^\/print-tags\/[^/]+\/edit$/.test(normalised)) {
        return kept;
    }
    if (normalised === '/reports') return normalised;
    // "My print tags" keeps the selected order in the URL (/print-tags/:id or ?order=): picking another one
    // must not reload the list. The designer (/print-tags/new, /print-tags/:id/edit) still remounts.
    if (/^\/print-tags(\/(?!new$)[^/]+)?$/.test(normalised)) return '/print-tags';
    return `${pathname}${search}`;
};

const App = ({history, match, location, isBulkMessagingEnabled, isPopupEnabled, isWithoutOverdue, isLoadedPhoneNumbers, isNfcSignsEnabled, isPrintTagsEnabled}) => {

    if (!isWithoutOverdue && !['/new-plans', '/balance', '/payments'].includes(document.location.pathname)) {
        history.push('/new-plans');
        return null;
    }

    // Embedded in Shoper: messaging only (see util/shoperLimits).
    if (isWithoutOverdue && isShoperLimited() && isHiddenInShoper(location.pathname)) {
        return <Redirect to="/my-dashboard"/>;
    }

    return (
        <div className="gx-main-content-wrapper no-padding">
            <Switch key={remountKey(location)}>
                <Route path={`${match.url}plans`}
                       component={LazyPlansPlanlist}/>
                <Route path={`${match.url}payments`}
                       component={LazyPayments}/>
                <Route path={`${match.url}balance/:action?`}
                       component={LazyBilling}/>
                <Route path={`${match.url}new-plan`}
                       component={LazyPlansNewplan} exact/>
                <Route path={`${match.url}new-plan/sms`}
                       component={LazyPlansSmsplan} exact/>
                <Route path={`${match.url}new-plan/whatsapp`}
                       component={LazyPlansWhatsappplan} exact/>
                <Route path={`${match.url}new-plans`}
                       component={LazyBilling} exact/>
                <Route path={`${match.url}shoper/login`}
                       component={LazyShoperLogin}/>
                <Route path={`${match.url}shoper/mobile-login`}
                       component={LazyShoperMobilelogin}/>
                <Route path={`${match.url}channels/mobile-login`}
                       component={LazyChannelsMobilelogin}/>
                <Route path={`${match.url}channels/choose-account`}
                       component={LazyChannelsChooseaccount}/>
                <Route path={`${match.url}channels/login`}
                       component={LazyChannelsLogin}/>
                <Route path={`${match.url}livechat/login`}
                       component={LazyLivechatLogin}/>
                <Route path={`${match.url}aws-marketplace/login`}
                       component={LazyAwsmarketplaceLogin} exact/>
                <Route path={`${match.url}videos/player/:videoId/:linkId?`}
                       component={LazyVideosVideoplayer} exact/>
                <Route path={`${match.url}videos/upload/:videoKey`}
                       component={LazyVideosVideomobileuploader} exact/>
                <Route path={`${match.url}print-tags/upload/:videoKey`}
                       component={LazyPrinttagsArtworkMobileUploader} exact/>
                <Route path={`${match.url}qr-codes`}
                       render={() => <Redirect to="/qr-and-nfc?tab=qr"/>} exact/>
                <Route path={`${match.url}qr-and-nfc`}
                       component={LazyQrandnfc} exact/>
                <Route path={`${match.url}qr-code/:uuid`}
                       component={LazyQrcode} exact/>
                <Route path={`${match.url}newsletter/:campaignName`}
                       component={LazyNewsletter} exact/>
                <Route path={`${match.url}stop/:campaignName`}
                       component={LazyNewsletterunsubscribingform} exact/>
                <Route path={`${match.url}unsubscribing/:contactId`}
                       component={LazyNewsletterunsubscribing} exact/>
                <Route path={`${match.url}aws-marketplace/setup-form`}
                       component={LazyAwsmarketplaceSetupform} exact/>

                {isLoadedPhoneNumbers && isWithoutOverdue && (
                    <>
                        <Route path={`${match.url}no-phone-number`}
                               component={LazyNophonenumber}/>
                        <Route path={`${match.url}my-dashboard`}
                               component={LazyWidgetNewsletterdashboard}/>
                        <Route path={`${match.url}chat`}
                               render={() => <ChatNew isArchive={false}/>}/>
                        <Route path={`${match.url}my-archives`}
                               render={() => <ChatNew isArchive={true}/>}/>
                        <Route path={`${match.url}settings/livechat`}
                               component={LazySettingsLivechat}/>
                        <Route path={`${match.url}settings/intercom`}
                               component={LazySettingsIntercom}/>
                        <Route path={`${match.url}settings/slack`}
                               component={LazySettingsSlack}/>
                        <Route path={`${match.url}billing-data`}
                               component={LazyBillingdata}/>
                        <Route path={`${match.url}phone-numbers/edit/:id`}
                               render={({match: m}) => <Redirect to={isBulkMessagingEnabled
                                   ? `/settings?section=phone-numbers&number=${m.params.id}`
                                   : `/phone-numbers?number=${m.params.id}`}/>} exact/>
                        {isBulkMessagingEnabled ? (
                            <Route path={`${match.url}phone-numbers`}
                                   render={() => <Redirect to="/settings?section=phone-numbers"/>} exact/>
                        ) : (
                            <Route path={`${match.url}phone-numbers`}
                                   component={LazyPhonenumbers} exact/>
                        )}

                        <Route path={`${match.url}form-texts/:id`}
                               component={LazyFormtextsFormtextedit} exact/>
                        <Route path={`${match.url}form-texts`}
                               component={LazyFormtexts} exact/>

                        {isBulkMessagingEnabled && (
                            <>
                                <Route path={`${match.url}message-templates/new`}
                                       component={LazyMessagetemplatesMessagetemplateadd}
                                       exact/>
                                <Route path={`${match.url}message-templates/edit/:templateName`}
                                       component={LazyMessagetemplatesMessagetemplateedit}
                                       exact/>
                                <Route path={`${match.url}message-templates`}
                                       component={LazyMessagetemplates} exact/>
                                <Route path={`${match.url}saved-replies`}
                                       component={LazySavedReplies} exact/>
                                {/* Old address of the campaign form: Shoper's "SMS Marketing" still opens it.
                                    The Shoper app starts on the dashboard; elsewhere it is the campaign form. */}
                                <Route path={`${match.url}bulk/sms`} exact
                                       render={() => <Redirect to={isShoperLimited() ? '/my-dashboard' : '/bulk/message'}/>}/>
                                <Route path={`${match.url}bulk/message`}
                                       component={LazyBulkMessage}/>
                                <Route path={`${match.url}contacts/import`}
                                       component={LazyContactsContactsfileimport} exact/>
                                {/* Only a contact id (or an old link's number), so /contacts/import and other
                                    /contacts/… pages never also render the contact page. */}
                                <Route path={`${match.url}contacts/:contactKey([0-9a-fA-F]{24}|\\d{6,15})`}
                                       component={LazyContactsContactdetails} exact/>
                                <Route path={`${match.url}contacts`}
                                       component={LazyContactsHub} exact/>
                                <Route path={`${match.url}settings`}
                                       component={LazySettings} exact/>
                                <Route path={`${match.url}unsubscribe-page-settings`}
                                       component={LazySettingsUnsubscribingpagesettings} exact/>
                                <Route path={`${match.url}urls/new`}
                                       component={LazyUrlsUrladd} exact/>
                                <Route path={`${match.url}urls/edit/:url`}
                                       component={LazyUrlsUrledit} exact/>
                                <Route path={`${match.url}urls`}
                                       component={LazyUrls} exact/>
                                <Route path={`${match.url}block-list/new`}
                                       render={() => <Redirect to="/contacts?tab=blocklist"/>} exact/>
                                <Route path={`${match.url}block-list/imports`}
                                       render={() => <Redirect to="/contacts?tab=imports&type=blocklist"/>} exact/>
                                <Route path={`${match.url}block-list/imports/add`}
                                       render={() => <Redirect to="/contacts/import?type=blocklist"/>} exact/>
                                <Route path={`${match.url}block-list`}
                                       render={() => <Redirect to="/contacts?tab=blocklist"/>} exact/>
                                <Route path={`${match.url}videos/new`}
                                       render={() => <Redirect to="/media?tab=videos&upload=1"/>} exact/>
                                <Route path={`${match.url}videos/edit/:videoId`}
                                       component={LazyVideosVideoedit} exact/>
                                <Route path={`${match.url}videos`}
                                       render={() => <Redirect to="/media?tab=videos"/>} exact/>
                                <Route path={`${match.url}media`}
                                       component={LazyMedia} exact/>
                                <Route path={`${match.url}custom-fields/new`}
                                       render={() => <Redirect to="/contacts?tab=tags"/>} exact/>
                                <Route path={`${match.url}custom-fields`}
                                       render={() => <Redirect to="/contacts?tab=tags"/>} exact/>
                                <Route path={`${match.url}reports/bulks/:bulkId`}
                                       component={LazyBulkDetails} exact/>
                                <Route path={`${match.url}reports/bulks`}
                                       component={LazyBulkReports} exact/>
                                <Route path={`${match.url}reports/contact-imports`}
                                       render={() => <Redirect to="/contacts?tab=imports"/>}/>
                                <Route path={`${match.url}reports/chats`}
                                       render={() => <Redirect to="/reports?type=chats"/>}/>
                                <Route path={`${match.url}reports/newsletter`}
                                       render={() => <Redirect to="/reports?type=signups"/>}/>
                                <Route path={`${match.url}reports/nfc`}
                                       render={() => <Redirect to="/reports?type=nfc"/>}/>
                                <Route path={`${match.url}analytics`}
                                       component={LazyAnalytics} exact/>
                                <Route path={`${match.url}reports`}
                                       component={LazyReports} exact/>
                                <Route path={`${match.url}keywords/edit/:id`}
                                       component={LazyCampaignkeywordsCampaignkeywordedit}
                                       exact/>
                                <Route path={`${match.url}keywords/new`}
                                       component={LazyCampaignkeywordsCampaignkeywordadd}
                                       exact/>
                                <Route path={`${match.url}automations`}
                                       component={LazyAutomations} exact/>
                                <Route path={`${match.url}keywords`}
                                       component={LazyCampaignkeywords} exact/>
                                <Route path={`${match.url}contact-tags`}
                                       render={() => <Redirect to="/contacts?tab=tags"/>} exact/>
                                <Route path={`${match.url}webhooks/new`}
                                       render={() => <Redirect to="/webhooks?new=1"/>} exact/>
                                <Route path={`${match.url}webhooks/:id/edit`}
                                       render={({match: m}) => <Redirect to={`/webhooks/${m.params.id}?edit=1`}/>} exact/>
                                <Route path={`${match.url}webhooks/:id/statuses`}
                                       render={({match: m}) => <Redirect to={`/webhooks/${m.params.id}`}/>} exact/>
                                <Route path={`${match.url}webhooks/:id`}
                                       component={LazyWebhooksWebhookdetails}
                                       exact/>
                                <Route path={`${match.url}webhooks`}
                                       component={LazyWebhooks}
                                       exact/>
                                <Route path={`${match.url}nfc-tag-groups`}
                                       render={() => <Redirect to="/qr-and-nfc?tab=groups"/>}
                                       exact/>
                                <Route path={`${match.url}nfc-tag-groups/new`}
                                       component={LazyNfctaggroupsNfctaggroupadd}
                                       exact/>
                                <Route path={`${match.url}nfc-tag-groups/:id/edit`}
                                       component={LazyNfctaggroupsNfctaggroupedit}
                                       exact/>
                                <Route path={`${match.url}notifications`}
                                       render={() => <Redirect to="/settings?section=notifications"/>}
                                       exact/>

                                {isPopupEnabled && (
                                    <>
                                        <Route path={`${match.url}images/new`}
                                               render={() => <Redirect to="/media?tab=images&upload=1"/>} exact/>
                                        <Route path={`${match.url}images`}
                                               render={() => <Redirect to="/media?tab=images"/>} exact/>
                                        <Route path={`${match.url}popups/edit/:id`}
                                               component={LazyWidgetPopupsPopupedit}
                                               exact/>
                                        <Route path={`${match.url}popups/new`}
                                               component={LazyWidgetPopupsPopupadd}
                                               exact/>
                                        <Route path={`${match.url}popups/installation`}
                                               component={LazyWidgetPopupsPopupinstallation}
                                               exact/>
                                        <Route path={`${match.url}popups`}
                                               render={() => <Redirect to="/forms?type=popup"/>} exact/>
                                        <Route path={`${match.url}forms`}
                                               component={LazyWidgetForms} exact/>
                                        <Route path={`${match.url}coupons/import/list`}
                                               component={LazyCouponsCouponimportslist}
                                               exact/>
                                        <Route path={`${match.url}coupons/import`}
                                               component={LazyCouponsCouponsimport}
                                               exact/>
                                        <Route path={`${match.url}coupons`}
                                               component={LazyCouponsCouponslist} exact/>
                                        <Route path={`${match.url}landing-pages/edit/:id`}
                                               component={LazyWidgetLandingpagesLandingpageedit}
                                               exact/>
                                        <Route path={`${match.url}landing-pages/new`}
                                               component={LazyWidgetLandingpagesLandingpageadd}
                                               exact/>
                                        <Route path={`${match.url}landing-pages`}
                                               render={() => <Redirect to="/forms?type=landing"/>} exact/>
                                        <Route path={`${match.url}unsubscribe-pages/edit/:id`}
                                               component={LazyWidgetUnsubscribingpagesUnsubscribingpageedit}
                                               exact/>
                                        <Route path={`${match.url}unsubscribe-pages/new`}
                                               component={LazyWidgetUnsubscribingpagesUnsubscribingpageadd}
                                               exact/>
                                        <Route path={`${match.url}unsubscribe-pages`}
                                               render={() => <Redirect to="/forms?type=unsubscribe"/>} exact/>
                                        <Route path={`${match.url}embedded-newsletters/edit/:id`}
                                               component={LazyWidgetEmbeddednewslettersEmbeddednewsletteredit}
                                               exact/>
                                        <Route path={`${match.url}embedded-newsletters/new`}
                                               component={LazyWidgetEmbeddednewslettersEmbeddednewsletteradd}
                                               exact/>
                                        <Route path={`${match.url}embedded-newsletters/installation`}
                                               component={LazyWidgetEmbeddednewslettersEmbeddednewsletterinstallation}
                                               exact/>
                                        <Route path={`${match.url}embedded-newsletters`}
                                               render={() => <Redirect to="/forms?type=embedded"/>}
                                               exact/>
                                        <Route path={`${match.url}widget-integrations`}
                                               component={LazyWidgetIntegrations}
                                               exact/>
                                    </>
                                )}
                            </>
                        )}

                        {isWithoutOverdue && (
                            <>
                                <Route path={`${match.url}qr-codes/new`}
                                       component={LazyQrcodesQrcodeadd} exact/>
                                <Route path={`${match.url}qr-codes/edit/:id`}
                                       component={LazyQrcodesQrcodeedit} exact/>
                            </>
                        )}

                        {/* Its own Switch: inside these fragments the outer Switch no longer picks one route, so
                            /print-tags/new would also match /print-tags/:id. Order ids are Mongo ids. */}
                        {isWithoutOverdue && isPrintTagsEnabled && (
                            <Switch>
                                <Route path={`${match.url}print-tags`}
                                       component={LazyPrinttags} exact/>
                                <Route path={`${match.url}print-tags/new`}
                                       component={LazyPrinttagsDesigner} exact/>
                                {/* Order e-mails link to /print-tags/orders/:id */}
                                <Route path={`${match.url}print-tags/orders/:id(${PRINT_TAG_ORDER_ID})`}
                                       render={({match: m}) => <Redirect to={`/print-tags/${m.params.id}`}/>} exact/>
                                <Route path={`${match.url}print-tags/:id(${PRINT_TAG_ORDER_ID})/edit`}
                                       component={LazyPrinttagsDesigner} exact/>
                                <Route path={`${match.url}print-tags/:id(${PRINT_TAG_ORDER_ID})`}
                                       component={LazyPrinttags} exact/>
                            </Switch>
                        )}

                        {isWithoutOverdue && isNfcSignsEnabled && (
                            <>
                                <Route path={`${match.url}nfc-signs`}
                                       render={() => <Redirect to="/qr-and-nfc?tab=signs"/>} exact/>
                                <Route path={`${match.url}nfc-signs/edit/:id`}
                                       component={LazyNfcsignsNfcsignedit} exact/>
                            </>
                        )}
                    </>
                )}

            </Switch>
        </div>
    );
};

const mapStateToProps = (state) => {
    const isBulkMessagingEnabled = (process.env.REACT_APP_IS_BULK_MESSAGING_ENABLED === "true" || state.accounts.bulkMessagingEnabled) && state.global.permissions?.includes(BULK_MESSAGING);
    const isPopupEnabled = state.accounts.popupEnabled;
    const isWithoutOverdue = state.accounts.status !== AccountStatus.OVERDUE;
    const isLoadedPhoneNumbers = state.accounts.isLoadedPhoneNumbers;
    const isNfcSignsEnabled = state.accounts.nfcSignsEnabled;
    const isPrintTagsEnabled = state.accounts.printTagsEnabled;

    return {isBulkMessagingEnabled, isPopupEnabled, isWithoutOverdue, isLoadedPhoneNumbers, isNfcSignsEnabled, isPrintTagsEnabled}
};

export default withRouter(connect(mapStateToProps)(App));

